import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePlanner } from '../context/PlannerContext';
import StickyNoteCard from './StickyNoteCard';
import { stickyStyle } from '../utils/stickyColors';
import { parseStickyContent } from '../utils/stickyContent';

const FAN_X_SM = 28;
const FAN_Y_SM = 26;
const COLLAPSED_SCALE = 0.42;
const EDGE_PAD = 8;
/** Mini-Post-its über dem offenen Zettel */
const PEEK_W = 108;
const PEEK_H = 118;
const PEEK_GAP = 12;
const PEEK_ROW_H = PEEK_H + 18;
const HOVER_MS = 120;

const clampPercent = (value, min, max) => Math.max(min, Math.min(max, value));

const fitShift = (left, top, width, height, stageW, stageH, pad = EDGE_PAD) => {
  let dx = 0;
  let dy = 0;
  if (left + width > stageW - pad) dx = stageW - pad - (left + width);
  if (top + height > stageH - pad) dy = stageH - pad - (top + height);
  if (left + dx < pad) dx = pad - left;
  if (top + dy < pad) dy = pad - top;
  return { x: dx, y: dy };
};

const noteLabel = (note) => {
  const content = parseStickyContent(note.body || '');
  if (content.title?.trim()) return content.title.trim().slice(0, 18);
  if (content.notes?.trim()) return content.notes.trim().split('\n')[0].slice(0, 18);
  const items = content.items || [];
  if (items.length) {
    const open = items.filter((i) => !i.done).length;
    return `${open}/${items.length}`;
  }
  return 'Note';
};

const StickyNotesOverlay = () => {
  const { notes, preferences, updateNote, deleteNote } = usePlanner();
  const [activeId, setActiveId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [stackPos, setStackPos] = useState({ x: 62, y: 18 });
  const [edgeShift, setEdgeShift] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const stackPosRef = useRef(stackPos);
  const stageRef = useRef(null);
  const visibleNotesRef = useRef([]);
  const updateNoteRef = useRef(updateNote);
  const moveHandlerRef = useRef(null);
  const upHandlerRef = useRef(null);
  const leaveTimerRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const suppressSyncRef = useRef(false);
  const hoveredIdRef = useRef(null);

  const visibleNotes = useMemo(
    () => (notes || []).filter((n) => n.showOnOverlay && !n.done),
    [notes]
  );

  visibleNotesRef.current = visibleNotes;
  updateNoteRef.current = updateNote;
  hoveredIdRef.current = hoveredId;
  const anchorNote = visibleNotes[0];

  useEffect(() => {
    stackPosRef.current = stackPos;
  }, [stackPos]);

  useEffect(() => {
    if (!anchorNote || isDragging || suppressSyncRef.current) return;
    const x = Number(anchorNote.posX);
    const y = Number(anchorNote.posY);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    const cur = stackPosRef.current;
    if (Math.abs(cur.x - x) < 0.05 && Math.abs(cur.y - y) < 0.05) return;
    setStackPos({ x: clampPercent(x, 0, 100), y: clampPercent(y, 0, 100) });
  }, [anchorNote?.id, anchorNote?.posX, anchorNote?.posY, isDragging]);

  useEffect(() => {
    if (!visibleNotes.length) {
      setActiveId(null);
      return;
    }
    if (!activeId || !visibleNotes.some((n) => String(n.id) === String(activeId))) {
      setActiveId(visibleNotes[visibleNotes.length - 1].id);
    }
  }, [visibleNotes, activeId]);

  useLayoutEffect(() => {
    // Beim Ziehen keine Edge-Korrektur — sonst kann man nicht am rechten Rand parken
    if (!hoveredId || isDragging) {
      setEdgeShift({ x: 0, y: 0 });
      return;
    }
    const stage = stageRef.current;
    if (!stage) return;
    const note = visibleNotes.find((n) => String(n.id) === String(hoveredId));
    if (!note) return;
    const behind = visibleNotes.filter((n) => String(n.id) !== String(hoveredId));
    const stageW = stage.clientWidth;
    const stageH = stage.clientHeight;
    const left = (stackPos.x / 100) * stageW;
    const top = (stackPos.y / 100) * stageH;
    const peekRowW = behind.length * (PEEK_W + PEEK_GAP);
    const width = Math.max(note.width || 260, peekRowW);
    const height = PEEK_ROW_H + (note.height || 280);
    setEdgeShift(fitShift(left, top, width, height, stageW, stageH));
  }, [hoveredId, stackPos.x, stackPos.y, visibleNotes, isDragging]);

  useEffect(() => () => {
    if (moveHandlerRef.current) window.removeEventListener('pointermove', moveHandlerRef.current);
    if (upHandlerRef.current) {
      window.removeEventListener('pointerup', upHandlerRef.current);
      window.removeEventListener('pointercancel', upHandlerRef.current);
    }
    if (leaveTimerRef.current) window.clearTimeout(leaveTimerRef.current);
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
  }, []);

  const boundsForStack = () => {
    const stage = stageRef.current;
    const stageW = stage?.clientWidth || window.innerWidth;
    const stageH = stage?.clientHeight || window.innerHeight;
    const count = Math.max(1, visibleNotesRef.current.length);
    const stackW = 260 * COLLAPSED_SCALE + Math.max(0, count - 1) * FAN_X_SM;
    const stackH = 280 * COLLAPSED_SCALE + Math.max(0, count - 1) * FAN_Y_SM;
    return {
      stageW,
      stageH,
      maxX: Math.max(0, ((stageW - stackW) / stageW) * 100),
      maxY: Math.max(0, ((stageH - stackH) / stageH) * 100),
    };
  };

  const endStackDragListeners = () => {
    if (moveHandlerRef.current) {
      window.removeEventListener('pointermove', moveHandlerRef.current);
      moveHandlerRef.current = null;
    }
    if (upHandlerRef.current) {
      window.removeEventListener('pointerup', upHandlerRef.current);
      window.removeEventListener('pointercancel', upHandlerRef.current);
      upHandlerRef.current = null;
    }
  };

  const isInteractiveTarget = (target) =>
    !!target?.closest?.(
      'textarea,input,.sticky-note__resize,.sticky-note__todo,.sticky-note__check,.sticky-note__toolbar'
    );

  const clearLeaveTimer = () => {
    if (leaveTimerRef.current) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };

  const clearHoverTimer = () => {
    if (hoverTimerRef.current) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const activateNote = (noteId, immediate = false) => {
    if (dragRef.current) return;
    clearLeaveTimer();
    clearHoverTimer();
    if (immediate || String(hoveredIdRef.current) === String(noteId)) {
      setHoveredId(noteId);
      setActiveId(noteId);
      return;
    }
    hoverTimerRef.current = window.setTimeout(() => {
      if (dragRef.current) return;
      setHoveredId(noteId);
      setActiveId(noteId);
      hoverTimerRef.current = null;
    }, HOVER_MS);
  };

  const onStackLeave = () => {
    clearHoverTimer();
    clearLeaveTimer();
    leaveTimerRef.current = window.setTimeout(() => {
      if (!dragRef.current) setHoveredId(null);
    }, 260);
  };

  const beginStackDrag = (e, noteId) => {
    if (e.button !== 0) return;
    if (isInteractiveTarget(e.target)) return;

    // Beim Ziehen kompakt bleiben — Bounds = kleine Größe, Rand erreichbar
    clearHoverTimer();
    setHoveredId(null);
    setEdgeShift({ x: 0, y: 0 });
    if (noteId) setActiveId(noteId);

    e.preventDefault();
    endStackDragListeners();
    const { stageW, stageH, maxX, maxY } = boundsForStack();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: stackPosRef.current.x,
      origY: stackPosRef.current.y,
      stageW,
      stageH,
      maxX,
      maxY,
      moved: false,
    };
    setIsDragging(true);

    const onMove = (ev) => {
      if (!dragRef.current) return;
      const d = dragRef.current;
      const dx = ((ev.clientX - d.startX) / d.stageW) * 100;
      const dy = ((ev.clientY - d.startY) / d.stageH) * 100;
      if (Math.abs(dx) > 0.15 || Math.abs(dy) > 0.15) d.moved = true;
      const next = {
        x: clampPercent(d.origX + dx, 0, d.maxX),
        y: clampPercent(d.origY + dy, 0, d.maxY),
      };
      stackPosRef.current = next;
      setStackPos(next);
    };

    const onUp = async () => {
      if (!dragRef.current) return;
      const moved = dragRef.current.moved;
      const finalPos = { ...stackPosRef.current };
      dragRef.current = null;
      endStackDragListeners();
      setHoveredId(null);
      setEdgeShift({ x: 0, y: 0 });
      if (!moved) {
        setIsDragging(false);
        return;
      }
      const { maxX: mx, maxY: my } = boundsForStack();
      const next = {
        x: clampPercent(finalPos.x, 0, mx),
        y: clampPercent(finalPos.y, 0, my),
      };
      stackPosRef.current = next;
      setStackPos(next);
      suppressSyncRef.current = true;
      await Promise.all(
        visibleNotesRef.current.map((note) =>
          updateNoteRef.current(note.id, { posX: next.x, posY: next.y })
        )
      );
      setIsDragging(false);
      window.setTimeout(() => {
        suppressSyncRef.current = false;
      }, 80);
    };

    moveHandlerRef.current = onMove;
    upHandlerRef.current = onUp;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  if (!preferences.postitsAlwaysVisible) return null;
  if (!visibleNotes.length) return null;

  const activeNote =
    visibleNotes.find((n) => String(n.id) === String(activeId)) ||
    visibleNotes[visibleNotes.length - 1];

  const anyExpanded = hoveredId != null && !isDragging;
  const behindNotes = anyExpanded
    ? visibleNotes.filter((n) => String(n.id) !== String(hoveredId))
    : [];
  const behindCount = Math.max(0, visibleNotes.length - 1);
  const baseW = activeNote.width || 260;
  const baseH = activeNote.height || 280;
  const peekRowW = behindNotes.length * (PEEK_W + PEEK_GAP);
  const stackW = anyExpanded
    ? Math.max(baseW, peekRowW)
    : baseW * COLLAPSED_SCALE + behindCount * FAN_X_SM;
  const stackH = anyExpanded
    ? PEEK_ROW_H + baseH
    : baseH * COLLAPSED_SCALE + behindCount * FAN_Y_SM;

  return createPortal(
    <div className="sticky-overlay" aria-live="polite">
      <div ref={stageRef} className="sticky-overlay__stage">
        {visibleNotes.length === 1 ? (
          <SoloSticky
            note={visibleNotes[0]}
            updateNote={updateNote}
            deleteNote={deleteNote}
          />
        ) : (
          <div
            className={`sticky-overlay__stack ${isDragging ? 'is-dragging' : ''} ${anyExpanded ? 'is-expanded' : 'is-collapsed'}`}
            style={{
              left: `${stackPos.x}%`,
              top: `${stackPos.y}%`,
              width: stackW,
              height: stackH,
              transform: `translate(${anyExpanded ? edgeShift.x : 0}px, ${anyExpanded ? edgeShift.y : 0}px)`,
            }}
            onMouseLeave={onStackLeave}
          >
            {/* Alle anderen Zettel sichtbar ÜBER dem offenen — Hover vor und zurück */}
            {anyExpanded && (
              <div className="sticky-overlay__peek-row" role="tablist" aria-label="Weitere Notizen">
                {behindNotes.map((note, peekIndex) => {
                  const colors = stickyStyle(note.color);
                  const tilt = ((peekIndex % 3) - 1) * 3.5;
                  return (
                    <button
                      key={note.id}
                      type="button"
                      role="tab"
                      className="sticky-note sticky-overlay__peek-note"
                      style={{
                        width: PEEK_W,
                        height: PEEK_H,
                        backgroundColor: colors.bg,
                        color: colors.ink,
                        '--sticky-bg': colors.bg,
                        '--sticky-ink': colors.ink,
                        transform: `rotate(${tilt}deg)`,
                      }}
                      title={noteLabel(note)}
                      onMouseEnter={() => activateNote(note.id)}
                      onClick={() => activateNote(note.id, true)}
                      onPointerDown={(e) => {
                        if (e.button !== 0) return;
                        activateNote(note.id, true);
                        beginStackDrag(e, note.id);
                      }}
                    >
                      <span className="sticky-note__curl" aria-hidden />
                      <span
                        className="sticky-note__tape"
                        style={{ background: colors.tape }}
                        aria-hidden
                      />
                      <span className="sticky-overlay__peek-note-body">
                        {noteLabel(note)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {visibleNotes.map((note, index) => {
              const isExpanded = String(hoveredId) === String(note.id);

              // Im geöffneten Modus: nur den aktiven Zettel als große Karte zeigen
              if (anyExpanded && !isExpanded) return null;

              const scale = anyExpanded ? 1 : COLLAPSED_SCALE;
              const x = anyExpanded ? 0 : index * FAN_X_SM;
              const y = anyExpanded ? PEEK_ROW_H : index * FAN_Y_SM;
              const rotate = anyExpanded
                ? 0
                : (index - (visibleNotes.length - 1) / 2) * 2;

              return (
                <div
                  key={note.id}
                  data-note-id={note.id}
                  className={`sticky-overlay__card ${isExpanded ? 'is-expanded' : 'is-behind is-collapsed'}`}
                  style={{
                    zIndex: isExpanded ? 40 : 10 + index,
                    transform: `translate(${x}px, ${y}px) rotate(${rotate}deg) scale(${scale})`,
                  }}
                  onMouseEnter={() => {
                    // Fächer: Hover öffnet. Offener Zettel: Hover hält ihn offen.
                    activateNote(note.id);
                  }}
                  onPointerDown={(e) => {
                    activateNote(note.id, true);
                    beginStackDrag(e, note.id);
                  }}
                >
                  <StickyNoteCard
                    note={{
                      ...note,
                      rotation: 0,
                      width: note.width || 260,
                      height: note.height || 280,
                    }}
                    draggable={false}
                    resizable={isExpanded}
                    className={`sticky-overlay__note sticky-overlay__note--stacked ${isExpanded ? '' : 'sticky-overlay__note--peek'}`}
                    onChange={(patch) => updateNote(note.id, patch)}
                    onDelete={() => deleteNote(note.id)}
                    onToggleDone={(done) => updateNote(note.id, { done })}
                    onToggleOverlay={(showOnOverlay) => updateNote(note.id, { showOnOverlay })}
                    onResizeEnd={(size) => updateNote(note.id, size)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

const SoloSticky = ({ note, updateNote, deleteNote }) => {
  const [expanded, setExpanded] = useState(false);
  const [pos, setPos] = useState({
    x: Number(note.posX) || 62,
    y: Number(note.posY) || 18,
  });
  const [edgeShift, setEdgeShift] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const posRef = useRef(pos);
  const leaveTimer = useRef(null);
  const dragRef = useRef(null);
  const shellRef = useRef(null);
  const suppressSync = useRef(false);

  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  useEffect(() => {
    if (dragRef.current || suppressSync.current) return;
    const x = Number(note.posX);
    const y = Number(note.posY);
    if (Number.isFinite(x) && Number.isFinite(y)) setPos({ x, y });
  }, [note.posX, note.posY]);

  useLayoutEffect(() => {
    if (!expanded || dragging) {
      setEdgeShift({ x: 0, y: 0 });
      return;
    }
    const stage = shellRef.current?.closest('.sticky-overlay__stage');
    if (!stage) return;
    setEdgeShift(
      fitShift(
        (pos.x / 100) * stage.clientWidth,
        (pos.y / 100) * stage.clientHeight,
        note.width || 260,
        note.height || 280,
        stage.clientWidth,
        stage.clientHeight
      )
    );
  }, [expanded, dragging, pos.x, pos.y, note.width, note.height]);

  const isInteractiveTarget = (target) =>
    !!target?.closest?.(
      'textarea,input,button,a,.sticky-note__resize,.sticky-note__todo,.sticky-note__check,.sticky-note__toolbar'
    );

  const onPointerDown = (e) => {
    if (e.button !== 0 || isInteractiveTarget(e.target)) return;
    e.preventDefault();
    setExpanded(false);
    setEdgeShift({ x: 0, y: 0 });
    setDragging(true);
    const stage = e.currentTarget.closest('.sticky-overlay__stage');
    const stageW = stage?.clientWidth || window.innerWidth;
    const stageH = stage?.clientHeight || window.innerHeight;
    const noteW = (note.width || 260) * COLLAPSED_SCALE;
    const noteH = (note.height || 280) * COLLAPSED_SCALE;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: posRef.current.x,
      origY: posRef.current.y,
      stageW,
      stageH,
      maxX: Math.max(0, ((stageW - noteW) / stageW) * 100),
      maxY: Math.max(0, ((stageH - noteH) / stageH) * 100),
      moved: false,
    };

    const onMove = (ev) => {
      if (!dragRef.current) return;
      const d = dragRef.current;
      const dx = ((ev.clientX - d.startX) / d.stageW) * 100;
      const dy = ((ev.clientY - d.startY) / d.stageH) * 100;
      if (Math.abs(dx) > 0.15 || Math.abs(dy) > 0.15) d.moved = true;
      const next = {
        x: clampPercent(d.origX + dx, 0, d.maxX),
        y: clampPercent(d.origY + dy, 0, d.maxY),
      };
      posRef.current = next;
      setPos(next);
    };

    const onUp = async () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (!dragRef.current) return;
      const moved = dragRef.current.moved;
      dragRef.current = null;
      setDragging(false);
      setExpanded(false);
      setEdgeShift({ x: 0, y: 0 });
      if (!moved) return;
      const next = { ...posRef.current };
      suppressSync.current = true;
      setPos(next);
      await updateNote(note.id, { posX: next.x, posY: next.y });
      window.setTimeout(() => {
        suppressSync.current = false;
      }, 80);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const showExpanded = expanded && !dragging;

  return (
    <div
      ref={shellRef}
      className={`sticky-overlay__solo ${showExpanded ? 'is-expanded' : 'is-collapsed'}`}
      style={{
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        transform: `translate(${showExpanded ? edgeShift.x : 0}px, ${showExpanded ? edgeShift.y : 0}px) scale(${showExpanded ? 1 : COLLAPSED_SCALE})`,
      }}
      onMouseEnter={() => {
        if (dragRef.current) return;
        if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
        setExpanded(true);
      }}
      onMouseLeave={() => {
        if (dragRef.current) return;
        leaveTimer.current = window.setTimeout(() => setExpanded(false), 180);
      }}
      onPointerDown={onPointerDown}
    >
      <StickyNoteCard
        note={{ ...note, rotation: 0 }}
        draggable={false}
        resizable={showExpanded}
        className="sticky-overlay__note sticky-overlay__note--solo"
        onChange={(patch) => updateNote(note.id, patch)}
        onDelete={() => deleteNote(note.id)}
        onToggleDone={(done) => updateNote(note.id, { done })}
        onToggleOverlay={(showOnOverlay) => updateNote(note.id, { showOnOverlay })}
        onResizeEnd={(size) => updateNote(note.id, size)}
      />
    </div>
  );
};

export default StickyNotesOverlay;
